import * as editorTypes from "../editor-utils/editor-types";
import { CssSVGClasses } from "../editor-utils/editor-types";

/**
 * SvgHandler is an object that provides utility methods to handle SVG content.
 * @type SvgHandlerType
 */
export const SvgHandler: editorTypes.SvgHandlerType = {
    /**
     * @param svgString The SVG string to parse.
     * @returns A Promise that resolves with the parsed SVG Document.
     */
    Parse: (svgString: string) => {
        const SVG_NAMESPACE = 'http://www.w3.org/2000/svg';
        const SVG_MIME_TYPE = 'image/svg+xml';
        const parser = new DOMParser();
        try {
            // Parse the provided SVG string to an SVG Document.           
            const svgDocument = parser.parseFromString(svgString, SVG_MIME_TYPE);

            if (svgDocument.documentElement.nodeName !== 'svg') {
                {
                    throw new Error('Parsed content is not a valid SVG.');
                }
            }

            return svgDocument;
        } catch (error) {
            const emptySvg = parser.parseFromString(`<svg xmlns="${SVG_NAMESPACE}"></svg>`, SVG_MIME_TYPE);
            return emptySvg;
        }
    },
    /**
     * @param svgDocument The SVG Document to serialize.
     * @returns The blob URL string of the serialized SVG content or null in case of error.
     */
    Serialize: (svgDocument: Document) => {
        const SVG_MIME_TYPE = 'image/svg+xml';
        try {
            // Serialize the provided SVG Document
            const serializer = new XMLSerializer();
            const svgString = serializer.serializeToString(svgDocument);

            // Convert the serialized SVG to a blob URL
            const blob = new Blob([svgString], { type: SVG_MIME_TYPE });
            const blobUrl = URL.createObjectURL(blob);

            return blobUrl;
        } catch (error) {
            console.error("Error serializing SVG content:", error);
            return null;
        }
    },

    SerializeString: (svgDocument: Document) => {
        try {
            // Serialize the provided SVG Document
            const serializer = new XMLSerializer();
            const svgString = serializer.serializeToString(svgDocument);

            return svgString;
        } catch (error) {
            console.error("Error serializing SVG content:", error);
            return null;
        }
    }
}

/**
 * Fetches an SVG image from a given URL and returns it as a File object.
 * 
 * This function sends a fetch request to the provided URL, retrieves the SVG image as a blob,
 * and then converts it to a File object with the name 'newSvg.svg' and type 'image/svg+xml'.
 * 
 * @param {string} assetSectionImageURL - The URL of the SVG image to be fetched.
 * 
 * @returns {Promise<File>} - A promise that resolves to a File object containing the fetched SVG image.
 */
export async function FetchSVGAsFile(assetSectionImageURL: string): Promise<File> {
    try {
        const response = await fetch(assetSectionImageURL);

        if (!response.ok) {
            console.error("Failed to fetch the SVG image.");
        }

        const blob = await response.blob();
        const file = new File([blob], 'newSvg.svg', { type: 'image/svg+xml' });
        return file;
    } catch (e) {
        console.error("Error happen in FetchSVGAsFile", e);
        const defaultBlob = new Blob([], { type: 'image/svg+xml' });
        return new File([defaultBlob], 'default.svg', { type: 'image/svg+xml' });
    }
}

/**
 * Removes temporary classes from SVG content.
 *
 * This function asynchronously processes SVG content, specifically targeting
 * elements with temporary classes used for certain operations or visual styles.
 * It fetches the SVG content from a provided URL, parses it, and then removes
 * specified temporary classes from elements within the SVG. Finally, it serializes
 * the modified SVG document back into a string.
 *
 * @param {string} svgContent - The URL of the SVG content to be processed.
 * @returns {Promise<string>} A promise that resolves to a string representing the
 *                            modified SVG content. If an error occurs, it returns
 *                            an empty string.
 *
 * Usage:
 * ```
 * const updatedSvg = await RemoveTempClassesFromSvg("http://example.com/my.svg");
 * ```
 */
export async function RemoveTempClassesFromSvg(svgContent: string): Promise<string> {
    try {
        const response = await fetch(svgContent as string);
        const content = await response.text();
        const svgDoc = await SvgHandler.Parse(content);

        // Define the classes you want to remove
        const classToRemove1 = CssSVGClasses.WsReferenceTemp;
        const classToRemove2 = CssSVGClasses.WsWearReferencePlaneTemp;

        const pathsWithClasses = svgDoc.querySelectorAll(`path.${classToRemove1}, path.${classToRemove2}`);

        // Remove the specified classes from each of these elements
        pathsWithClasses.forEach(element => {
            element.classList.remove(classToRemove1, classToRemove2);
        });

        const svgFile = SvgHandler.Serialize(svgDoc);

        return svgFile as string;
    } catch (e) {
        console.error("Error in RemoveTempClassesFromSvg", e);
        return '';
    }
}

/**
 * Asynchronously removes all elements with a specific class ('ws-regionTemp') from an SVG document.
 *
 * This function takes a string representing the SVG content, typically a URL or path. It fetches
 * the SVG content, parses it into a document, and then searches for and removes all elements that 
 * have the 'ws-regionTemp' class. The updated SVG content, with the specified elements removed, 
 * is then serialized back into a string.
 *
 * @param {string} svgContent - The URL or path of the SVG content to be processed.
 * @returns {Promise<string>} - A promise that resolves to the updated SVG content as a string.
 *
 * The function performs the following actions:
 * 1. Fetches and retrieves the SVG content from the specified URL or path.
 * 2. Parses the retrieved content into an SVG document.
 * 3. Selects all elements in the document with the 'ws-regionTemp' class and removes them.
 * 4. Serializes the updated SVG document back into a string.
 *
 * @throws {Error} - An error is logged if there's an issue in fetching, parsing, or processing 
 *                   the SVG content. In such cases, an empty string is returned.
 *
 * Note: This function assumes the presence of a global `SvgHandler` object with `Parse` and 
 *       `Serialize` methods for handling SVG content, and a `CssSVGClasses` enum for class names.
 */
export async function RemmoveTempPathes(svgContent: string): Promise<string>{
    try {
        const response = await fetch(svgContent as string);
        const content = await response.text();
        const svgDoc = await SvgHandler.Parse(content);

        // Remove elements with the 'ws-regionTemp' class
        const tempRegionElements = svgDoc.querySelectorAll(`.${CssSVGClasses.WsRegionTemp}`);
        tempRegionElements.forEach(element => {
            element.remove();
        });

        const svgFile = SvgHandler.Serialize(svgDoc);

        return svgFile as string;
    } catch (e) {
        console.error("Error in RemoveTempPaths", e);
        return '';
    }
}

/**
 * removeUnwantedClasses - Removes classes from an SVG path element that are not specified in a hard-coded list.
 * 
 * @param {SVGPathElement} element - The SVG path element from which classes will be removed.
 * 
 * This function does the following:
 * 1. Checks if the SVG path element has any classes. If not, the function returns immediately.
 * 2. Iterates through each class present on the SVG path element.
 * 3. If a class is not in the hard-coded list of classes to preserve ('classesToPreserve'), it is removed.
 * 
 * The hard-coded 'classesToPreserve' list contains classes that should not be removed from the SVG path element.
 */
export function RemoveUnListedClasses(element: SVGPathElement): void {
    try {
        // Hard-coded list of classes to preserve
        const classesToPreserve: CssSVGClasses[] = [CssSVGClasses.SelectedPath, CssSVGClasses.WsLiner, CssSVGClasses.WsReferenceTemp, CssSVGClasses.WsRegionTemp, CssSVGClasses.WsHole];  // Add any other classes you want to preserve

        // Get all current classes into an array
        const currentClasses = Array.from(element.classList);

        // If no classes are present, exit the function early
        if (currentClasses.length === 0) {
            return;
        }

        // Iterate over each class and remove it if it's not in classesToPreserve
        currentClasses.forEach(className => {
            if (!classesToPreserve.includes(className as CssSVGClasses)) {
                element.classList.remove(className);
            }
        });
    } catch (e) {
        console.error("Error in RemoveUnListedClasses", e);
    }
}

export const ResetSelectedPath = (selectedPathId: string) => {
    if (selectedPathId) {
        const pathElement = document.getElementById(selectedPathId) as SVGPathElement | null;
        if (pathElement) {
            pathElement.classList.remove(CssSVGClasses.SelectedPath);
        }
    }
}

export async function ClearSVGBeforeSave(assetSectionImage: string): Promise<File> {
    try {
        let filterSvg = await RemoveTempClassesFromSvg(assetSectionImage);
        filterSvg = await RemmoveTempPathes(assetSectionImage);
        return await FetchSVGAsFile(filterSvg);
    } catch (e) {
        console.error('Error in: ClearSVGBeforeSave', e)
        return new File([], "empty.svg", { type: "image/svg+xml" });
    }
}


export const fetchSvgImageFromUrl = async (assetSectionImage: string): Promise<{ svgDocument: Document, renderedSvg: SVGSVGElement | null }> => {
    const response = await fetch(assetSectionImage);
    const content = await response.text();
    const svgDocument = await SvgHandler.Parse(content); // Ensure this matches your actual API
    const renderedSvg = document.querySelector('svg'); // This assumes there's a single <svg> element

    return { svgDocument, renderedSvg };
};

export const updateAssetSectionImage = async (svgDocument: Document): Promise<string|null> => {
    return await SvgHandler.Serialize(svgDocument);
};